import { Box, PopperPlacementType } from '@mui/material';
import React from 'react';

import { PopperSubmenuContainer } from '../Components/PopperSubmenuContainer';
import { DEFAULT_HEADER_ERROR_MESSAGE } from '../Components/utils';
import { useMenuContext } from '../ContextState/useMenuContext';
import { LevelThreeContainer } from '../LevelThree/LevelThreeContainer';
import { LevelTwoContainer } from '../LevelTwo/LevelTwoContainer';

import { DATA_TEST } from '../enums';
import { IMenuOption, MuiSxProps } from '../types';

import { itemHasNestedSubmenus, itemIsLeafNode, itemShouldFetchSubmenuSections } from '../utils/utils';

interface ISubmenuPopperContainerProps {
  currentLevelOneItem?: IMenuOption;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  popperStyleOverrides?: MuiSxProps;
  contentStyleOverrides?: MuiSxProps;
  gridWidth?: string;
  placement?: PopperPlacementType;
  customHeaderErrorMessage?: string;
  disablePortal?: boolean;
  handleClose?(e?: MouseEvent | TouchEvent): void;
}

export const SubmenuPopperContainer: React.FC<ISubmenuPopperContainerProps> = ({
  anchorEl,
  isOpen,
  popperStyleOverrides,
  currentLevelOneItem,
  handleClose,
  placement,
  customHeaderErrorMessage
}: ISubmenuPopperContainerProps) => {
  const {
    submenuLoadingItemId,
    setSubmenuLoadingItemId,
    fetchLevelTwoSubmenuSections,
    setLevelTwoLoadingError,
    setSelectedLevelTwoId,
    selectedLevelTwoId,
    setLevelThreeLoadingError,
    fetchLevelThreeSubmenuSections,
    levelTwoLoadingError,
    levelThreeLoadingError
  } = useMenuContext();
  const menuItemHasLevelThreeSections = itemHasNestedSubmenus(currentLevelOneItem);

  const showLevelTwoLoadingIndicator = submenuLoadingItemId === currentLevelOneItem?.id;
  const showLevelThreeLoadingIndicator = selectedLevelTwoId && submenuLoadingItemId === selectedLevelTwoId;

  const selectedLevelTwoItem = currentLevelOneItem?.submenuSections
    ?.flatMap((section) => section.items)
    .find((item) => item?.id === selectedLevelTwoId);

  const shouldFetchSecondLevelSubmenuItems = itemShouldFetchSubmenuSections(currentLevelOneItem);
  const shouldFetchThirdLevelSubmenuItems = itemShouldFetchSubmenuSections(selectedLevelTwoItem);

  const levelTwoSubmenuSections = currentLevelOneItem?.submenuSections || [];
  const levelThreeSubmenuSections = selectedLevelTwoItem?.submenuSections || [];

  // Fetch the second level submenu items if they have not been fetched yet
  React.useEffect(() => {
    if (shouldFetchSecondLevelSubmenuItems) {
      setSubmenuLoadingItemId(currentLevelOneItem?.id);
      // tslint:disable-next-line:no-floating-promises
      fetchLevelTwoSubmenuSections(currentLevelOneItem as IMenuOption)
        .then((res) => res)
        .catch(setLevelTwoLoadingError)
        .finally(() => {
          setSubmenuLoadingItemId(undefined);
        });
    }
  }, [shouldFetchSecondLevelSubmenuItems]);

  // Auto select the first item in the submenu if it is not a leaf node
  React.useEffect(() => {
    const firstLevelTwoSubsectionItem = levelTwoSubmenuSections?.flatMap((section) => section.items)[0];
    if (firstLevelTwoSubsectionItem && !itemIsLeafNode(firstLevelTwoSubsectionItem)) {
      setSelectedLevelTwoId(firstLevelTwoSubsectionItem.id);
    }
  }, [levelTwoSubmenuSections]);

  // Fetch the third level submenu items if they have not been fetched yet
  React.useEffect(() => {
    if (shouldFetchThirdLevelSubmenuItems) {
      setSubmenuLoadingItemId(selectedLevelTwoId ?? undefined);
      setLevelThreeLoadingError(false);
      // tslint:disable-next-line:no-floating-promises
      fetchLevelThreeSubmenuSections(selectedLevelTwoItem as IMenuOption)
        .then(() => {})
        .catch(setLevelThreeLoadingError)
        .finally(() => {
          setSubmenuLoadingItemId(undefined);
        });
    }
  }, [shouldFetchThirdLevelSubmenuItems]);

  return (
    <PopperSubmenuContainer
      anchorEl={anchorEl}
      dataTest={DATA_TEST.main_submenu_container}
      positionOffset={{ x: 0, y: 4 }}
      open={isOpen}
      handleClose={(e?: MouseEvent | TouchEvent) => handleClose?.(e)}
      styleOverrides={popperStyleOverrides}
      placement={placement}
      disablePortal={true}
    >
      <Box
        sx={{
          display: 'flex',
          maxHeight: '80vh',
          width:
            !menuItemHasLevelThreeSections || showLevelTwoLoadingIndicator || levelTwoLoadingError ? undefined : '80vw'
        }}
      >
        <LevelTwoContainer
          submenuSections={levelTwoSubmenuSections}
          showLoadingIndicator={showLevelTwoLoadingIndicator}
          headerErrorMessage={customHeaderErrorMessage || DEFAULT_HEADER_ERROR_MESSAGE}
          showErrorMessage={levelTwoLoadingError}
        />
        {menuItemHasLevelThreeSections && (
          <LevelThreeContainer
            submenuSections={levelThreeSubmenuSections}
            showLoadingIndicator={Boolean(showLevelThreeLoadingIndicator)}
            showErrorMessage={levelThreeLoadingError}
            headerErrorMessage={customHeaderErrorMessage || DEFAULT_HEADER_ERROR_MESSAGE}
          />
        )}
      </Box>
    </PopperSubmenuContainer>
  );
};
